import { useEffect, useState, type ReactNode } from "react";
import {
  canonicalJson,
  D,
  diffSnapshots,
  freeze,
  split1256,
  ZERO,
  type Analysis,
  type FuturesResult,
  type ModuleResult,
  type Person,
  type PropResult,
  type SeView,
  type SnapshotDiff,
  type YearFile,
} from "@ftr/tax-core";
import { Card, WhyPanel } from "../components/ui.tsx";
import { download } from "../lib/backup.ts";
import { dollars, useLicense, type Price } from "../lib/license.ts";
import { deleteSnapshot, listSnapshots, newId, nowIso, saveSnapshot, type SavedSnapshot } from "../lib/yearStore.ts";
import { useOrganizer } from "./context.tsx";
import { personName, STATE_LABEL, StateBadge } from "./common.tsx";
import { fmtUsd } from "./FuturesStep.tsx";
import { TextInput } from "./fields.tsx";
import { Link } from "../lib/router.tsx";

const EXCLUDED_WHY: Record<string, string> = {
  wrong_year: "for a different tax year",
  superseded: "replaced by a corrected form",
  voided: "voided by the payer",
  replacement_pending: "waiting for you to confirm a correction",
  corrected_unlinked: "marked CORRECTED but not linked to the form it replaces",
  chain_unresolved: "part of corrections that conflict",
  duplicate_unresolved: "one of two or more forms for the same account or payer, waiting for your answer",
  unassigned: "not tied to a person, account or firm",
};

function valueOf<T>(r: ModuleResult<T> | undefined): T | null {
  if (!r || r.state === "not_applicable") return null;
  return r.state === "blocked" ? r.partial : r.value;
}

/** Until the report is unlocked, lines show their labels and states but not their figures. */
const useLocked = () => useLicense().status !== "unlocked";

/** The "Why this amount?" steps, which carry the figures, only for an unlocked report. */
function Why({ r }: { r: ModuleResult<unknown> }) {
  const locked = useLocked();
  if (locked || (r.state !== "ready" && r.state !== "provisional")) return null;
  return <WhyPanel trace={r.trace} />;
}

const ordered = (people: Person[]) => [...people].sort((a, b) => (a.role === b.role ? 0 : a.role === "primary" ? -1 : 1));

export function ReportStep() {
  const { yf, analysis, go } = useOrganizer();
  const locked = useLocked();
  const blockers = analysis.issues.filter((i) => i.severity === "blocker").length;
  const reviews = analysis.issues.filter((i) => i.severity === "review").length;
  const nothing =
    analysis.modules.section1256.state === "not_applicable" && Object.values(analysis.modules.propBusiness).every((r) => r.state === "not_applicable");

  return (
    <div className="steps-body report">
      <div className="print-only print-head">
        <p className="print-brand">Futures Tax Report · {yf.taxYear} worksheet</p>
        <p className="muted small">
          {analysis.engineVersion} · rules {analysis.rulesVersion} · printed from this device. A worksheet for entering figures, not a tax return.
        </p>
      </div>

      {blockers > 0 ? (
        <div className="callout callout--warn">
          <strong>Not ready to file.</strong> {blockers} {blockers === 1 ? "item still needs" : "items still need"} information. Figures marked{" "}
          <em>not ready</em> are shown so you can see progress, but don't enter them on a return.{" "}
          <button type="button" className="link-btn no-print" onClick={() => go("review")}>
            Go to Review →
          </button>
        </div>
      ) : reviews > 0 ? (
        <div className="callout callout--warn">
          <strong>Almost there.</strong> {reviews} {reviews === 1 ? "item needs" : "items need"} a look before you file. The figures below are complete.{" "}
          <button type="button" className="link-btn no-print" onClick={() => go("review")}>
            Go to Review →
          </button>
        </div>
      ) : (
        <div className="callout">
          <strong>Ready.</strong> Every figure below is backed by the documents and answers you entered.
        </div>
      )}

      {nothing && blockers === 0 && <p className="card-intro">You don't have futures accounts or prop firms listed, so there's nothing to report for {yf.taxYear}.</p>}
      {locked && !nothing && <Unlock />}

      <FuturesReport />
      <PropReport />
      <SeReport />
      <Withholding />
      <Notes />
      {!locked && <Versions />}
    </div>
  );
}

function NotReady({ state }: { state: ModuleResult<unknown>["state"] }) {
  return state === "blocked" ? <span className="pill pill--blocked">not ready</span> : null;
}

function Line(props: { n?: string; label: ReactNode; value: string | null; note?: ReactNode; strong?: boolean; blocked?: boolean }) {
  const locked = useLocked();
  return (
    <div className={"rline" + (props.strong ? " rline--strong" : "") + (props.blocked ? " rline--blocked" : "")}>
      <span className="rline-label">
        {props.n && <span className="line-no">{props.n}</span>}
        {props.label}
        {props.note && <span className="rline-note">{props.note}</span>}
      </span>
      {locked && props.value !== null ? (
        <span className="rline-value rline-value--locked" aria-label="Shown when the report is unlocked">
          $•,•••
        </span>
      ) : (
        <span className="rline-value">{props.value === null ? "—" : fmtUsd(props.value)}</span>
      )}
    </div>
  );
}

/** One Form 6781 per return: the household for a joint return, each spouse for separate returns. */
function returnsFor(yf: YearFile): { label: string | null; personIds: string[] | null }[] {
  if (yf.household.filingStatus === "mfs") return ordered(yf.people).map((p) => ({ label: personName(yf, p.id), personIds: [p.id] }));
  return [{ label: null, personIds: null }];
}

function FuturesReport() {
  const { yf, analysis } = useOrganizer();
  const r = analysis.modules.section1256;
  if (r.state === "not_applicable") return null;
  const v = valueOf<FuturesResult>(r);
  const blocked = r.state === "blocked";
  return (
    <Card title="Form 6781, Part I: Section 1256 contracts">
      <div className="module-line">
        <StateBadge state={r.state} />
        <span>Gains and losses from regulated futures, marked to market at year-end and taxed 60% long-term, 40% short-term.</span>
      </div>
      {returnsFor(yf).map((ret) => (
        <Form6781 key={ret.label ?? "household"} label={ret.label} personIds={ret.personIds} v={v} analysis={analysis} blocked={blocked} />
      ))}
      <Why r={r} />
      <details className="fine">
        <summary>How to enter this</summary>
        <ul>
          <li>Enter each line 1 row as a Section 1256 contract (Form 6781) in your filing software, using the broker's 1099-B box 11 amount as printed.</li>
          <li>Lines 8 and 9 flow to Schedule D lines 4 and 11. Your software does this for you; don't also enter the trades on Form 8949.</li>
          <li>Not included: the loss carryback election (line 6, box D), Part II straddles, Part III unrecognized gains, and any loss carryover from earlier years.</li>
          <li>
            Step by step: <Link href="/guides/turbotax">TurboTax</Link>, <Link href="/guides/hr-block">H&R Block</Link> or{" "}
            <Link href="/guides/freetaxusa">FreeTaxUSA</Link>.
          </li>
        </ul>
      </details>
    </Card>
  );
}

function Form6781({ label, personIds, v, analysis, blocked }: { label: string | null; personIds: string[] | null; v: FuturesResult | null; analysis: Analysis; blocked: boolean }) {
  const { yf } = useOrganizer();
  const locked = useLocked();
  const accounts = (v?.perAccount ?? []).filter((a) => personIds === null || (a.ownerId !== "joint" && personIds.includes(a.ownerId)));
  // Line 1 lists each 1099-B separately; a statement-based account is one row.
  const rows: { id: string; label: string; amount: string }[] = [];
  for (const a of accounts) {
    if (a.basisFrom === "no_activity") continue;
    if (a.basisFrom === "statement") {
      rows.push({ id: a.accountId, label: `${a.label} (year-end statement)`, amount: a.basis });
      continue;
    }
    const recon = analysis.reconciliation.futures.find((x) => x.accountId === a.accountId);
    for (const f of recon?.forms ?? []) {
      if (!a.sourceIds.includes(f.sourceId) || f.box11.state !== "value") continue;
      const src = yf.sources.find((s) => s.id === f.sourceId);
      const issuer = src && "issuer" in src ? src.issuer : a.label;
      rows.push({ id: f.sourceId, label: `${issuer}${a.label && issuer !== a.label ? ` · ${a.label}` : ""}`, amount: f.box11.value });
    }
  }
  const losses = rows.reduce((s, x) => (new D(x.amount).isNeg() ? s.plus(x.amount) : s), ZERO);
  const gains = rows.reduce((s, x) => (new D(x.amount).isNeg() ? s : s.plus(x.amount)), ZERO);
  const net = gains.plus(losses);
  const split = split1256(net).value;
  return (
    <div className="worksheet">
      {label && <h3 className="sub-head">{label}'s Form 6781</h3>}
      <div className={"lines" + (blocked ? " lines--blocked" : "")}>
        {rows.length === 0 && <p className="muted small">No 1099-B or statement figures yet.</p>}
        {rows.map((x) => (
          <Line key={x.id} n="1" label={x.label} value={x.amount} note={new D(x.amount).isNeg() ? "Column (b), loss" : "Column (c), gain"} />
        ))}
        <Line n="2" label="Total losses (b) and total gains (c)" value={null} note={locked ? "Shown when unlocked" : `${fmtUsd(losses.toFixed())} and ${fmtUsd(gains.toFixed())}`} />
        <Line n="3" label="Net gain or (loss)" value={net.toFixed()} />
        <Line n="4–6" label="1099-B adjustments and loss carryback" value="0" note="None entered. See How to enter this." />
        <Line n="7" label="Net Section 1256 gain or (loss)" value={net.toFixed()} strong />
        <Line n="8" label={<>Short-term, 40% <NotReady state={blocked ? "blocked" : "ready"} /></>} value={split.shortTerm.toFixed()} note="To Schedule D line 4" />
        <Line n="9" label={<>Long-term, 60% <NotReady state={blocked ? "blocked" : "ready"} /></>} value={split.longTerm.toFixed()} note="To Schedule D line 11" strong />
      </div>
    </div>
  );
}

function PropReport() {
  const { yf, analysis } = useOrganizer();
  const people = ordered(yf.people).filter((p) => analysis.modules.propBusiness[p.id] && analysis.modules.propBusiness[p.id]!.state !== "not_applicable");
  if (!people.length) return null;
  return (
    <Card title="Schedule C: prop firm income">
      <p className="card-intro">
        Prop payouts are treated here as self-employment income from your trading business, reported on Schedule C. Each payout counts once, however
        many documents show it.
      </p>
      {people.map((p) => {
        const r = analysis.modules.propBusiness[p.id]!;
        const v = valueOf<PropResult>(r);
        const blocked = r.state === "blocked";
        const notDeducted = v ? Object.entries(v.byTreatment).filter(([t, amt]) => t !== "current" && !new D(amt).isZero()) : [];
        return (
          <div key={p.id} className="worksheet">
            <div className="module-line">
              <StateBadge state={r.state} />
              <span>{yf.people.length > 1 ? `${personName(yf, p.id)}'s Schedule C` : "Schedule C"}</span>
            </div>
            {v ? (
              <div className={"lines" + (blocked ? " lines--blocked" : "")}>
                {v.perFirm.map((f) => (
                  <Line key={f.firmId} label={f.name} value={f.grossIncome} note={`${f.payoutIds.length} ${f.payoutIds.length === 1 ? "payout" : "payouts"}${f.basis === "info_return_total" ? ", from the 1099 total" : ""}`} />
                ))}
                <Line n="1" label="Gross receipts" value={v.grossIncome} strong />
                {!new D(v.otherIncome).isZero() && <Line n="6" label="Other income" value={v.otherIncome} note="Refunds of costs you deducted in an earlier year" />}
                <Line n="28" label="Total expenses" value={v.currentExpenses} note="Payout fees and costs you marked as business expenses. Enter them by type in your software." />
                <Line n="31" label={<>Net profit or (loss) <NotReady state={r.state} /></>} value={v.netProfit} strong note="Before any home office deduction" />
                {notDeducted.map(([t, amt]) => (
                  <Line key={t} label={TREATMENT_NAME[t] ?? t} value={amt} note="Not deducted here" />
                ))}
              </div>
            ) : (
              <p className="muted small">Not enough information for figures yet.</p>
            )}
            <Why r={r} />
          </div>
        );
      })}
    </Card>
  );
}

const TREATMENT_NAME: Record<string, string> = {
  startup: "Startup costs",
  personal: "Personal costs",
  capital: "Equipment and other capital costs",
  unresolved: "Costs not yet classified",
};

function SeReport() {
  const { yf, analysis } = useOrganizer();
  const people = ordered(yf.people).filter((p) => analysis.modules.selfEmployment[p.id] && analysis.modules.selfEmployment[p.id]!.state !== "not_applicable");
  if (!people.length) return null;
  return (
    <Card title="Self-employment tax: a cross-check">
      <p className="card-intro">
        Your filing software works out self-employment tax from Schedule C. Use these figures to check it. <strong>Don't enter them as extra tax.</strong>
      </p>
      {people.map((p) => {
        const r = analysis.modules.selfEmployment[p.id]!;
        const v = valueOf<SeView>(r);
        return (
          <div key={p.id} className="worksheet">
            <div className="module-line">
              <StateBadge state={r.state} />
              <span>{yf.people.length > 1 ? `${personName(yf, p.id)}'s Schedule SE` : "Schedule SE"}</span>
            </div>
            {v ? (
              <div className={"lines lines--quiet" + (r.state === "blocked" ? " lines--blocked" : "")}>
                <Line label="Net earnings from self-employment" value={v.netEarnings} note="Schedule SE line 6" />
                <Line label="Social Security part" value={v.socialSecurityTax} />
                <Line label="Medicare part" value={v.medicareTax} />
                <Line label={<>Self-employment tax <NotReady state={r.state} /></>} value={v.seTax} strong note="Schedule SE line 12" />
                <Line label="Deductible half" value={v.deductibleHalf} note="Schedule SE line 13" />
              </div>
            ) : (
              <p className="muted small">Waiting on the figures above.</p>
            )}
            <Why r={r} />
          </div>
        );
      })}
      <p className="field-hint">Not included: the 0.9% Additional Medicare Tax (Form 8959), which depends on your household's total wages and self-employment income.</p>
    </Card>
  );
}

function Withholding() {
  const { yf, analysis } = useOrganizer();
  if (!analysis.withholding.length) return null;
  const total = analysis.withholding.reduce((s, w) => s.plus(w.amount), ZERO);
  return (
    <Card title="Federal tax withheld on your 1099s">
      <div className="lines">
        {analysis.withholding.map((w) => {
          const s = yf.sources.find((x) => x.id === w.sourceId);
          return <Line key={w.sourceId} label={s && "issuer" in s ? s.issuer || "Unnamed issuer" : "Form"} value={w.amount} note={w.personId && yf.people.length > 1 ? personName(yf, w.personId) : "Box 4"} />;
        })}
        <Line label="Total" value={total.toFixed()} strong note="Form 1040 line 25b. It's a payment toward your tax, not income." />
      </div>
    </Card>
  );
}

function Notes() {
  const { yf, analysis } = useOrganizer();
  const notes = yf.decisions.filter((d) => d.deletedAt === null && (d.kind === "accept_variance" || d.kind === "recognition_year"));
  const excluded = analysis.excludedSources;
  if (!notes.length && !excluded.length) return null;
  return (
    <Card title="Your notes and documents set aside">
      {notes.length > 0 && (
        <ul className="plain-list">
          {notes.map((d) => (
            <li key={d.id}>
              {d.kind === "accept_variance" ? `Difference of ${fmtUsd(d.variance)} kept as is: ${d.reason}` : d.kind === "recognition_year" ? d.reason : null}
            </li>
          ))}
        </ul>
      )}
      {excluded.length > 0 && (
        <>
          <p className="form-section-label">Not counted</p>
          <ul className="plain-list">
            {excluded.map((x) => {
              const s = yf.sources.find((y) => y.id === x.sourceId);
              return (
                <li key={x.sourceId}>
                  {s && "issuer" in s ? s.issuer || "Unnamed issuer" : "A document"}: {EXCLUDED_WHY[x.reason] ?? x.reason}
                </li>
              );
            })}
          </ul>
        </>
      )}
    </Card>
  );
}

/** What a locked report offers: the price, checkout, and ways back in for someone who already bought it. */
function Unlock() {
  const { yf } = useOrganizer();
  const { status, getPrice, checkout, recover } = useLicense();
  const [price, setPrice] = useState<Price | null | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    void getPrice().then((p) => live && setPrice(p));
    return () => {
      live = false;
    };
  }, [getPrice]);

  const buy = async () => {
    setError(null);
    setBusy(true);
    const msg = await checkout();
    // On success the page is leaving for checkout, so the button stays busy.
    if (msg) {
      setError(msg);
      setBusy(false);
    }
  };

  if (status === "checking")
    return (
      <Card className="unlock no-print">
        <p className="muted small">Checking for your purchase…</p>
      </Card>
    );
  const open = price?.available ? price : null;
  return (
    <Card title={`Unlock your ${yf.taxYear} report`} className="unlock no-print">
      <p className="card-intro">
        Entering your records and the Review are free. Unlocking shows every figure below, ready to type into your filing software or hand to your
        preparer, with the steps behind each one, print and PDF, downloads, and saved versions to compare when a corrected 1099 arrives.
      </p>
      {price === undefined ? (
        <p className="muted small">Loading the price…</p>
      ) : open ? (
        <p className="unlock-price">
          <strong>{dollars(open.amountCents)}</strong> for the {yf.taxYear} tax year
          {open.earlyBird && <span className="muted small"> · early-bird price through January 31, 2027, then {dollars(open.regularCents)}</span>}
        </p>
      ) : (
        <p className="muted small">Buying isn't open yet. Everything you enter is saved on this device, so nothing is lost while you wait.</p>
      )}
      <div className="editor-actions">
        <button type="button" className="btn btn--primary" onClick={buy} disabled={!open || busy}>
          {busy ? "Opening checkout…" : open ? `Unlock for ${dollars(open.amountCents)}` : "Unlock"}
        </button>
      </div>
      {error && <p className="field-msg">{error}</p>}
      <p className="field-hint">
        One payment, no subscription. Checkout is by Stripe. We receive your email and the payment, never your tax records, which stay in this browser.
      </p>
      <details className="fine">
        <summary>Already bought it?</summary>
        {open?.recovery && (
          <div className="form-section">
            <TextInput label="The email you used at checkout" value={email} onChange={setEmail} placeholder="you@example.com" />
            <button type="button" className="btn btn--outline btn--sm" onClick={async () => setSent(await recover(email))} disabled={!email.trim()}>
              Email me an unlock link
            </button>
            {sent && <p className="field-hint">{sent}</p>}
          </div>
        )}
        <p className="field-hint">
          On another device or browser? Download an encrypted backup there (under "Your data" at the bottom of the tax report) and restore it here. The
          unlock comes with it.
        </p>
      </details>
    </Card>
  );
}

function Versions() {
  const { yf } = useOrganizer();
  const [saved, setSaved] = useState<SavedSnapshot[] | null>(null);
  const [label, setLabel] = useState("");
  const [compare, setCompare] = useState<{ id: string; diff: SnapshotDiff | null; error?: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = () =>
    listSnapshots(yf.taxYear)
      .then(setSaved)
      .catch(() => setSaved([]));
  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yf.taxYear]);

  const save = async () => {
    setError(null);
    const { snapshot } = freeze(yf, { createdAt: nowIso() });
    try {
      await saveSnapshot({ id: newId(), taxYear: yf.taxYear, label: label.trim() || "Saved version", snapshot });
      setLabel("");
      await refresh();
    } catch {
      setError("This browser couldn't save the version. Download it instead.");
    }
  };

  const doCompare = (s: SavedSnapshot) => {
    try {
      const now = freeze(yf, { createdAt: nowIso() }).snapshot;
      setCompare({ id: s.id, diff: diffSnapshots(s.snapshot, now) });
    } catch (e) {
      setCompare({ id: s.id, diff: null, error: e instanceof Error ? e.message : String(e) });
    }
  };

  const exportJson = () => {
    const { snapshot } = freeze(yf, { createdAt: nowIso() });
    download(`futures-tax-report-${yf.taxYear}.json`, canonicalJson(snapshot));
  };

  return (
    <Card title="Save, print or download" className="no-print">
      <p className="card-intro">
        Save a version when you file or send figures to your preparer. If a corrected 1099 arrives later, compare against it to see exactly what changed.
      </p>
      <div className="editor-actions">
        <button type="button" className="btn btn--primary btn--sm" onClick={() => window.print()}>
          Print or save as PDF
        </button>
        <button type="button" className="btn btn--outline btn--sm" onClick={exportJson}>
          Download data (JSON)
        </button>
      </div>
      <div className="form-section">
        <TextInput label="Name this version" value={label} onChange={setLabel} placeholder="For example: Sent to my CPA" />
        <button type="button" className="btn btn--outline btn--sm" onClick={save}>
          Save this version
        </button>
        {error && <p className="field-msg">{error}</p>}
      </div>
      {saved && saved.length > 0 && (
        <div className="form-section">
          <p className="form-section-label">Saved versions</p>
          {saved.map((s) => (
            <div key={s.id} className="version">
              <div className="row">
                <div className="row-main">
                  <strong>{s.label}</strong>
                  <span className="muted small"> · {new Date(s.snapshot.createdAt).toLocaleString()}</span>
                </div>
                <div className="row-actions">
                  <button type="button" className="link-btn" onClick={() => (compare?.id === s.id ? setCompare(null) : doCompare(s))}>
                    {compare?.id === s.id ? "Hide changes" : "Compare with now"}
                  </button>
                  <button type="button" className="link-btn" onClick={() => download(`futures-tax-report-${s.taxYear}-${s.snapshot.createdAt.slice(0, 10)}.json`, canonicalJson(s.snapshot))}>
                    Download
                  </button>
                  <button type="button" className="link-btn link-btn--danger" onClick={() => confirm("Delete this saved version?") && deleteSnapshot(s.id).then(refresh)}>
                    Delete
                  </button>
                </div>
              </div>
              {compare?.id === s.id && <DiffView diff={compare.diff} error={compare.error} />}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function DiffView({ diff, error }: { diff: SnapshotDiff | null; error?: string }) {
  if (!diff) return <p className="field-msg">{error ?? "These versions can't be compared."}</p>;
  const changed = diff.figures.filter((f) => f.change === null || !new D(f.change).isZero());
  const docs = diff.sources.added.length + diff.sources.removed.length + diff.sources.revised.length;
  if (!changed.length && !diff.states.length && !docs) return <p className="good-text small">Nothing has changed since this version.</p>;
  return (
    <div className="diff">
      {changed.map((f) => (
        <div key={f.key} className="rline">
          <span className="rline-label">
            {f.label}
            <span className="rline-note">
              {f.before === null ? "new" : fmtUsd(f.before)} → {f.after === null ? "removed" : fmtUsd(f.after)}
            </span>
          </span>
          <span className={"rline-value" + (f.change && new D(f.change).isNeg() ? " loss-text" : "")}>{f.change === null ? "—" : signed(f.change)}</span>
        </div>
      ))}
      {diff.states.map((s) => (
        <p key={s.key} className="small">
          {s.label}: {label(s.before)} → {label(s.after)}
        </p>
      ))}
      {docs > 0 && (
        <p className="small muted">
          Documents: {diff.sources.added.length} added, {diff.sources.removed.length} removed, {diff.sources.revised.length} edited
          {diff.replaced.length ? `, ${diff.replaced.length} replaced by a corrected form` : ""}.
        </p>
      )}
    </div>
  );
}

function signed(v: string): string {
  const d = new D(v);
  return (d.isPos() && !d.isZero() ? "+" : "") + fmtUsd(v);
}

function label(state: string): string {
  return (STATE_LABEL as Record<string, string>)[state] ?? state;
}
